import { axiosPrivate } from "../api/axios";
import { useEffect, useRef } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

// Store CSRF token globally so it persists across component re-renders
let globalCsrfToken = null;

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth, setAuth } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    // Use refs to maintain stable references for the interceptors
    const refreshRef = useRef(refresh);
    const setAuthRef = useRef(setAuth);
    const navigateRef = useRef(navigate);
    const enqueueSnackbarRef = useRef(enqueueSnackbar);

    // Update refs when dependencies change
    useEffect(() => {
        refreshRef.current = refresh;
        setAuthRef.current = setAuth;
        navigateRef.current = navigate;
        enqueueSnackbarRef.current = enqueueSnackbar;
    });

    useEffect(() => {
        const requestIntercept = axiosPrivate.interceptors.request.use(
            config => {
                // Read from localStorage first for freshest auth (critical for impersonation)
                const storedAuth = JSON.parse(localStorage.getItem('auth') || '{}');
                const currentAuth = storedAuth?.accessToken ? storedAuth : auth;

                if (!config.headers['Authorization'] && currentAuth?.accessToken) {
                    config.headers['Authorization'] = `Bearer ${currentAuth.accessToken}`;
                }

                // Add language header for backend multi-language support
                const currentLang = localStorage.getItem('i18nextLng') || 'lng1';
                config.headers['X-Language'] = currentLang;

                // Add CSRF token for state-changing requests
                if (config.method === 'post' ||
                    config.method === 'put' ||
                    config.method === 'delete' ||
                    config.method === 'patch') {

                    if (globalCsrfToken) {
                        config.headers['X-CSRF-TOKEN'] = globalCsrfToken;
                    }
                }

                return config;
            }, (error) => Promise.reject(error)
        );

        const responseIntercept = axiosPrivate.interceptors.response.use(
            response => {
                // Store CSRF token from response headers (case-insensitive)
                const csrfToken = response.headers['x-csrf-token'] || response.headers['X-CSRF-TOKEN'];
                if (csrfToken) {
                    globalCsrfToken = csrfToken;
                    //console.log('CSRF token received and stored:', csrfToken);
                }
                return response;
            },
            async (error) => {
                const prevRequest = error?.config;
                const errorCode = error?.response?.data?.error;
                const errorMessage = error?.response?.data?.message;

                // Handle token expiration - try to refresh token for any 401 error on first attempt
                // This covers both explicit 'token_expired' errors and generic 401s that might be due to expired tokens
                if (error?.response?.status === 401 && !prevRequest?.sent) {
                    prevRequest.sent = true;
                    try {
                        const newAccessToken = await refreshRef.current();

                        // If refresh returns null (204 response), don't retry
                        if (!newAccessToken) {
                            const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
                            if (!isLoginPage) {
                                setAuthRef.current({});
                                localStorage.removeItem('auth');
                                navigateRef.current('/');
                                enqueueSnackbarRef.current('Your session has expired. Please log in again.', { variant: 'warning' });
                            }
                            return Promise.reject(error);
                        }

                        prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                        return axiosPrivate(prevRequest);
                    } catch (refreshError) {
                        // Refresh failed, logout user
                        const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
                        if (!isLoginPage) {
                            setAuthRef.current({});
                            localStorage.removeItem('auth');
                            navigateRef.current('/');
                            enqueueSnackbarRef.current('Your session has expired. Please log in again.', { variant: 'warning' });
                        }
                        return Promise.reject(refreshError);
                    }
                }
                // Handle authorization errors (403) - show permission error
                else if (error?.response?.status === 403) {
                    enqueueSnackbarRef.current(errorMessage || 'You don\'t have permission to perform this action.', { variant: 'error' });
                }
                // Handle not found errors
                else if (error?.response?.status === 404) {
                    console.warn('Resource not found:', error?.config?.url);
                }
                // Handle conflict errors
                else if (error?.response?.status === 409) {
                    enqueueSnackbarRef.current(errorMessage || 'Conflict with the current state of the resource.', { variant: 'warning' });
                }
                // Handle validation errors
                else if (error?.response?.status === 400) {
                    if (errorMessage) {
                        enqueueSnackbarRef.current(errorMessage, { variant: 'error' });
                    } else {
                        enqueueSnackbarRef.current('Invalid request. Please check your input.', { variant: 'error' });
                    }
                }
                // Handle server errors
                else if (error?.response?.status >= 500) {
                    enqueueSnackbarRef.current('Server error. Please try again later.', { variant: 'error' });
                    console.error('Server error:', error);
                }
                // Handle network errors
                else if (!error?.response && error?.request) {
                    if (error?.request?.status !== 0) { // Ignore cancelled requests
                        enqueueSnackbarRef.current('Network error. Please check your connection.', { variant: 'error' });
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axiosPrivate.interceptors.request.eject(requestIntercept);
            axiosPrivate.interceptors.response.eject(responseIntercept);
        }
        // Only need to set up interceptors once
        // eslint-disable-next-line
    }, []);

    return axiosPrivate;
}

export default useAxiosPrivate;
